import type { ReactNode } from 'react';
import { ArrowRightIcon } from '@heroicons/react/24/outline';
import { RichEditor, isV2PatientVersion } from '@odo/shared-ui';
import { usePatientCard } from '../../../../../hooks/usePatientCard';
import { useStatusCatalog } from '../../../../../services/statusCatalog';
import { StatusPill } from '../../../overview/shared';
import { CaseChecklistSummary } from './checklist/CaseChecklistSummary';
import type { TiptapDoc } from '@odo/shared-ui';
import type { ActionBy } from '../../../../../services/refinementTarget';

const NOOP_CHANGE = () => {};
const NOOP_UPLOAD = async (): Promise<{ url: string; fileId: string }> => {
    return { url: '', fileId: '' };
};

function actionNote(
    actionBy: ActionBy | null | undefined,
    actionRequired: string | null,
    reviewer: string
): string | undefined {
    if (actionBy === undefined) return `The ${reviewer} will review it`;
    return actionBy && actionRequired ? `For the ${actionBy}` : undefined;
}

interface VcaReviewStepProps {
    patientId: string;
    notesDoc: TiptapDoc;
    targetCaseStatus: string;
    targetActionRequired: string | null;
    targetActionBy?: ActionBy | null;
    targetInternalCaseStatus: string;
    notesPanelTitle: string;
    /** False on edit: the checklist was signed off on the original trigger. */
    showCaseChecklist: boolean;
}

function VcaReviewStep({
    patientId,
    notesDoc,
    targetCaseStatus,
    targetActionRequired,
    targetActionBy,
    targetInternalCaseStatus,
    notesPanelTitle,
    showCaseChecklist
}: VcaReviewStepProps) {
    const { data } = usePatientCard(patientId);
    const catalog = useStatusCatalog();
    const currentCaseStatus = data?.status?.caseStatus ?? null;
    const currentActionRequired = data?.status?.actionRequired ?? null;
    const currentInternalCaseStatus = data?.status?.internalCaseStatus ?? null;
    const reviewer = isV2PatientVersion(data?.status?.version) ? 'orthodontist' : 'GP';

    return (
        <div className="w-full max-w-2xl mx-auto flex flex-col gap-4">
            <div>
                <h3 className="text-sm font-semibold text-gray-800 mb-1">Review &amp; trigger</h3>
                <p className="text-xs text-gray-500">
                    Confirm the changes below. Submitting will update Monday and record this action
                    in the patient timeline.
                </p>
            </div>

            <RecapPanel title="Status updates">
                <StatusTransitionRow
                    label="Case status"
                    current={currentCaseStatus}
                    target={targetCaseStatus}
                />
                <StatusTransitionRow
                    label="Action required"
                    current={currentActionRequired}
                    target={targetActionRequired}
                    note={actionNote(targetActionBy, targetActionRequired, reviewer)}
                />
                <StatusTransitionRow
                    label="Internal case status"
                    current={currentInternalCaseStatus}
                    target={targetInternalCaseStatus}
                    currentDisplay={
                        currentInternalCaseStatus
                            ? catalog.display(currentInternalCaseStatus)
                            : null
                    }
                    targetDisplay={catalog.display(targetInternalCaseStatus)}
                />
            </RecapPanel>

            {showCaseChecklist && (
                <RecapPanel title="Case checklist">
                    <CaseChecklistSummary />
                </RecapPanel>
            )}

            <RecapPanel title={notesPanelTitle}>
                <RichEditor
                    value={notesDoc}
                    readOnly
                    chrome="flush"
                    onChange={NOOP_CHANGE}
                    onImageUpload={NOOP_UPLOAD}
                />
            </RecapPanel>
        </div>
    );
}

interface RecapPanelProps {
    title: string;
    children: ReactNode;
}

function RecapPanel({ title, children }: RecapPanelProps) {
    return (
        <div className="rounded-xl border border-gray-200 bg-white p-4">
            <div className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
                {title}
            </div>
            {children}
        </div>
    );
}

interface StatusTransitionRowProps {
    label: string;
    current: string | null;
    target: string | null;
    // Display overrides for workflow ids; comparison stays on current/target
    // so ids are matched as ids, never as their rendered text.
    currentDisplay?: string | null;
    targetDisplay?: string;
    note?: string;
}

function StatusTransitionRow({
    label,
    current,
    target,
    currentDisplay,
    targetDisplay,
    note
}: StatusTransitionRowProps) {
    const unchanged = current === target;
    return (
        <div className="flex flex-col items-center gap-2 py-3 first:pt-1 last:pb-1">
            <span className="text-[11px] font-medium text-gray-400 uppercase tracking-wider">
                {label}
            </span>
            <div className="flex items-center justify-center gap-3 flex-wrap">
                <StatusPill value={currentDisplay ?? current} className="px-3 py-1 text-[12px]" />
                <ArrowRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                <StatusPill value={targetDisplay ?? target} className="px-3 py-1 text-[12px]" />
            </div>
            {unchanged && <span className="text-[11px] text-gray-400 italic">no change</span>}
            {note && <span className="text-[11px] text-gray-500">{note}</span>}
        </div>
    );
}

export { VcaReviewStep };
